<template>
  <div class="wsa-page">
    <PageHeader
      title="Web Scraping API"
      subtitle="Your credentials, a visual request builder, and usage stats for the Web Scraping API."
    />

    <v-row>
      <v-col cols="12" lg="6">
        <SectionCard title="Current subscription" class="wsa-page__card">
          <template v-if="!!subscription && !!subscription.plan">
            <dl class="wsa-page__meta">
              <div class="wsa-page__meta-row">
                <dt>Current plan</dt>
                <dd>
                  <router-link to="/subscriptions">{{
                    subscription.plan.name
                  }}</router-link>
                </dd>
              </div>
              <div class="wsa-page__meta-row">
                <dt>Start date</dt>
                <dd>{{ getUTCDateString(subscription.start_time) }}</dd>
              </div>
              <div class="wsa-page__meta-row">
                <dt>Renewal date</dt>
                <dd>{{ getUTCDateString(subscription.end_time) }}</dd>
              </div>
              <div class="wsa-page__meta-row">
                <dt>Auto-restart</dt>
                <dd>{{ api_renew_status ? "Enabled" : "Disabled" }}</dd>
              </div>
              <div class="wsa-page__meta-row">
                <dt>Concurrency limit</dt>
                <dd>
                  {{ subscription.plan.price === 0 ? "1" : "Unlimited" }}
                </dd>
              </div>
            </dl>

            <div class="wsa-page__credentials">
              <CredentialField
                label="API token"
                :value="api_token || ''"
                secret
              />
            </div>

            <StatCard
              class="wsa-page__stat"
              label="API credits used"
              :value="subscription.total_credits - subscription.remained_credits"
              :max="subscription.total_credits"
              :caption="creditsCaption"
            />

            <section
              v-if="freePlanGuidance"
              class="wsa-page__plan-guidance"
              aria-labelledby="api-plan-guidance-title"
            >
              <h3 id="api-plan-guidance-title">Planning more API requests?</h3>
              <p>
                <template v-if="freePlanGuidance.exhausted">
                  You've run out of API credits.
                </template>
                <template v-else>
                  You've used at least half of your free API credits. You can
                  keep using your remaining credits.
                </template>
                Compare API plans for more credits and parallel requests.
                Upgrading is optional.
              </p>
              <router-link to="/subscriptions">Compare API plans</router-link>
              <p v-if="freePlanGuidance.exhausted">
                For a custom plan,
                <a href="mailto:support@scrapingant.com">contact us</a>.
              </p>
            </section>
            <p v-else-if="subscription.remained_credits === 0" class="wsa-page__hint">
              You've run out of API credits. Please
              <router-link to="/subscriptions">upgrade your plan</router-link>
              or
              <a href="mailto:support@scrapingant.com">contact us</a>
              for a custom plan.
            </p>
          </template>
          <template v-else>
            <p class="wsa-page__empty">
              No active subscription. Please
              <a @click="reload">reload</a>
              the page or try again later.
            </p>
          </template>
        </SectionCard>
      </v-col>

      <v-col cols="12" lg="6">
        <div
          v-if="subscription && subscription.update_url"
          class="wsa-page__side"
        >
          <SectionCard
            title="Subscription restart"
            subtitle="Automatically restart your subscription when API credits run out."
          >
            <p class="wsa-page__side-status">
              Current status:
              <strong>{{ api_renew_status ? "Enabled" : "Disabled" }}</strong>
            </p>
            <template #actions>
              <v-btn
                text
                color="primary"
                @click="onChangeRenewStatusClicked"
              >{{ api_renew_status ? "Disable restart" : "Enable restart" }}</v-btn>
            </template>
          </SectionCard>

          <SectionCard
            title="Payment details"
            subtitle="Change or update the card on file via Paddle."
          >
            <template #actions>
              <v-btn
                text
                color="primary"
                @click="onUpdatePaymentDetailsClicked"
              >Update payment</v-btn>
            </template>
          </SectionCard>
        </div>
      </v-col>
    </v-row>

    <SectionCard
      title="Request generator"
      subtitle="Build a request, execute it, and grab a code snippet in your favorite language."
      class="wsa-page__generator"
    >
      <v-row>
        <v-col cols="12" lg="6">
          <v-text-field
            hide-details
            label="URL"
            v-model="request_generator.url"
            outlined
            dense
            class="mb-4"
          ></v-text-field>

          <v-text-field
            v-if="request_generator.response_type === 'extract'"
            hide-details
            label="Extract properties"
            v-model="request_generator.extract_properties"
            outlined
            dense
            class="mb-4"
          ></v-text-field>

          <v-row>
            <v-col cols="12" sm="4">
              <v-select
                :items="proxytype"
                label="Proxy type"
                item-text="name"
                item-value="value"
                v-model="request_generator.proxy_type"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                :items="proxycountry"
                label="Proxy country"
                item-text="name"
                item-value="value"
                v-model="request_generator.country"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                :items="response_types"
                label="Extraction type"
                item-text="name"
                item-value="value"
                v-model="request_generator.response_type"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
          </v-row>

          <v-row class="mt-3">
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-switch
                v-model="request_generator.browser"
                color="primary"
                class="mt-0 mr-3"
                hide-details
              ></v-switch>
              <span class="wsa-page__switch-label">Use headless browser</span>
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    size="18"
                    class="wsa-page__help"
                    v-bind="attrs"
                    v-on="on"
                  >mdi-help-circle-outline</v-icon>
                </template>
                <span>Enables headless browser for making the request</span>
              </v-tooltip>
            </v-col>
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-switch
                v-model="request_generator.return_page_source"
                color="primary"
                class="mt-0 mr-3"
                :disabled="!request_generator.browser"
                hide-details
              ></v-switch>
              <span class="wsa-page__switch-label">Return page source</span>
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    size="18"
                    class="wsa-page__help"
                    v-bind="attrs"
                    v-on="on"
                  >mdi-help-circle-outline</v-icon>
                </template>
                <span
                  >Returns the original HTML of the page (browser rendering
                  only)</span
                >
              </v-tooltip>
            </v-col>
          </v-row>

          <v-textarea
            outlined
            label="Cookies"
            height="96"
            hide-details
            v-model="request_generator.cookies"
            class="mt-4"
          ></v-textarea>
          <p class="wsa-page__field-hint">
            Format: cookie_name_1=cookie_value_1;cookie_name_2=cookie_value_2
            <a
              target="_blank"
              href="https://docs.scrapingant.com/custom-cookies"
              >Learn more</a
            >
          </p>

          <v-textarea
            outlined
            label="JS snippet"
            height="96"
            hide-details
            v-model="request_generator.js_snippet"
            :disabled="!request_generator.browser"
            class="mt-4"
          ></v-textarea>
          <p class="wsa-page__field-hint">
            JS snippet to run after the page loads (browser rendering only)
            <a
              target="_blank"
              href="https://docs.scrapingant.com/javascript-execution"
              >Learn more</a
            >
          </p>

          <v-textarea
            outlined
            label="Wait for CSS selector"
            height="96"
            hide-details
            v-model="request_generator.wait_for_selector"
            :disabled="!request_generator.browser"
            class="mt-4"
          ></v-textarea>
          <p class="wsa-page__field-hint">
            CSS selector to wait for before returning the result (browser
            rendering only)
            <a
              target="_blank"
              href="https://docs.scrapingant.com/wait-for-selector"
              >Learn more</a
            >
          </p>

          <div class="wsa-page__execute">
            <span
              v-if="request_generator.response_type === 'extract'"
              class="wsa-page__cost"
              >Extract cost is available after request</span
            >
            <span v-else class="wsa-page__cost"
              >Request cost: <strong>{{ requestCost }}</strong> API credits</span
            >
            <v-btn
              color="primary"
              :disabled="!request_generator.url"
              @click="executeRequestClicked"
            >Execute request</v-btn>
          </div>
        </v-col>

        <v-col cols="12" lg="6">
          <div class="wsa-page__code-panel">
            <v-tabs
              v-model="tab"
              color="primary"
              show-arrows
              class="wsa-page__tabs"
            >
              <v-tab
                v-for="item in tabItems"
                :key="item"
                class="wsa-page__tab"
              >{{ item }}</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab" class="wsa-page__tab-items">
              <v-tab-item v-for="(item, index) in tabItems" :key="item">
                <CodeBlock
                  :code="templates[index].get_template"
                  :language="templates[index].prism_language"
                  :label="codeBlockLabel(index)"
                />
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-col>
      </v-row>
    </SectionCard>

    <SectionCard title="Usage chart" class="wsa-page__chart">
      <v-progress-circular
        v-if="usage_grapph_loading"
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <usage-chart
        v-if="!usage_grapph_loading"
        :start-date="getUTCDateString(subscription.start_time)"
        :chart-data="chartdata"
      />
    </SectionCard>

    <v-dialog v-model="show_result_dialog" scrollable max-width="880">
      <v-card class="wsa-page__result-card">
        <div class="wsa-page__result-header">
          <h2>Request result</h2>
          <v-btn-toggle
            v-if="isHtmlResponse && request_dialog_result"
            v-model="result_view_mode"
            mandatory
            dense
            color="primary"
          >
            <v-btn value="code" small>
              <v-icon small left>mdi-code-tags</v-icon>Code
            </v-btn>
            <v-btn value="preview" small>
              <v-icon small left>mdi-eye</v-icon>Preview
            </v-btn>
          </v-btn-toggle>
        </div>

        <div class="wsa-page__result-body">
          <CodeBlock
            v-if="!!request_dialog_result && result_view_mode === 'code'"
            :code="request_dialog_result"
            :language="typeof raw_result === 'object' ? 'json' : 'html'"
          />
          <div
            v-if="
              !!request_dialog_result &&
              result_view_mode === 'preview' &&
              isHtmlResponse
            "
            class="wsa-page__preview"
          >
            <iframe
              :srcdoc="raw_result"
              sandbox="allow-same-origin"
              title="HTML Preview"
            ></iframe>
          </div>
          <div v-if="!request_dialog_result" class="wsa-page__result-notice">
            <h3>Request execution time might be up to 60 seconds</h3>
          </div>
          <p
            v-if="request_dialog_result"
            class="wsa-page__result-cost"
          >Request cost: <strong>{{ request_cost }}</strong> API credits</p>
          <div v-if="result_loading" class="wsa-page__result-spinner">
            <v-progress-circular
              :size="48"
              :width="2"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
        </div>

        <div class="wsa-page__result-actions">
          <RequestFeedback :generator-data="request_generator" />
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="!request_dialog_result"
            @click="clipboardForOutput(raw_result)"
          >Copy to clipboard</v-btn>
          <v-btn text @click="show_result_dialog = false">Close</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import UsageChart from "../views/UsageChart";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-markup-templating";
import "prismjs/components/prism-php";
import "prismjs/components/prism-java";
import "prismjs/components/prism-csharp";
import templates from "@/helper/languages_templates_helper";
import getDateUTCString from "@/utils/date_utils";
import constants from "@/config/constants";
import { firstTouchParams } from "@/utils/common_utils";
import RequestFeedback from "@/views/RequestFeedback";
import { getRequestCost } from "@/helper/request_price_helper";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import CredentialField from "@/components/shared/CredentialField";
import CodeBlock from "@/components/shared/CodeBlock";

const PRIMARY = "#E2782F";
const PRIMARY_ALPHA = "#E2782F99";
const TRANSPARENT = "#FFFFFF00";

const buildPrimaryDataset = (label, data) => ({
  label,
  lineTension: 0.2,
  borderWidth: 2,
  pointRadius: 2,
  backgroundColor: TRANSPARENT,
  borderColor: PRIMARY,
  pointBorderColor: PRIMARY,
  pointBackgroundColor: PRIMARY,
  data,
});

const buildSecondaryDataset = (label, data, i) => ({
  label,
  lineTension: 0.2,
  borderWidth: 2,
  pointRadius: 2,
  backgroundColor: PRIMARY_ALPHA,
  borderColor: PRIMARY_ALPHA,
  pointBorderColor: PRIMARY_ALPHA,
  pointBackgroundColor: PRIMARY_ALPHA,
  borderDash: [(i + 1) * 2, i + 1],
  data,
});

export default {
  name: "WebScrapingAPI",
  components: {
    RequestFeedback,
    UsageChart,
    PageHeader,
    SectionCard,
    StatCard,
    CredentialField,
    CodeBlock,
  },
  watch: {
    "$route.query"() {
      this.applyPrefillFromQuery();
    },
    "request_generator.browser"(val) {
      if (val === false) {
        this.request_generator.wait_for_selector = "";
        this.request_generator.js_snippet = "";
        this.request_generator.return_page_source = false;
      }
    },
  },
  data() {
    return {
      tab: null,
      tabItems: ["curl", "python", "nodejs", "php", "java", "c#", "proxy"],
      proxytype: [
        { name: "Standard", value: "datacenter" },
        { name: "Residential", value: "residential" },
      ],
      response_types: [
        { name: "Original", value: "general" },
        { name: "JSON (extended data)", value: "extended" },
        { name: "Markdown", value: "markdown" },
        { name: "AI extracted", value: "extract" },
      ],
      proxycountry: [
        { name: "All countries", value: "ALL" },
        { name: "USA", value: "US" },
        { name: "United Arab Emirates", value: "AE" },
        { name: "Brazil", value: "BR" },
        { name: "Canada", value: "CA" },
        { name: "China", value: "CN" },
        { name: "Czech Republic", value: "CZ" },
        { name: "Germany", value: "DE" },
        { name: "Spain", value: "ES" },
        { name: "France", value: "FR" },
        { name: "United Kingdom", value: "GB" },
        { name: "Hong Kong", value: "HK" },
        { name: "India", value: "IN" },
        { name: "Italy", value: "IT" },
        { name: "Israel", value: "IL" },
        { name: "Japan", value: "JP" },
        { name: "Netherlands", value: "NL" },
        { name: "Poland", value: "PL" },
        { name: "Russia", value: "RU" },
        { name: "Saudi Arabia", value: "SA" },
        { name: "Singapore", value: "SG" },
        { name: "Turkey", value: "TR" },
        { name: "South Korea", value: "KR" },
        { name: "Indonesia", value: "ID" },
        { name: "Vietnam", value: "VN" },
      ],
      request_generator: {
        url: constants.DEFAULT_REQUEST_GENERATOR_SITE,
        return_page_source: false,
        js_snippet: "",
        extract_properties: "title, content",
        cookies: "",
        country: "ALL",
        wait_for_selector: "",
        proxy_type: "datacenter",
        browser: true,
        response_type: "general",
      },
      api_token: "",
      prefilled_from_landing: false,
      show_result_dialog: false,
      request_dialog_result: "",
      raw_result: "",
      result_view_mode: "code",
      request_cost: 0,
      subscription: {},
      api_renew_status: false,
      chartdata: {
        labels: [],
        datasets: [buildPrimaryDataset("API credits usage", [])],
      },
      loading: false,
      result_loading: false,
      usage_grapph_loading: false,
    };
  },
  computed: {
    freePlanGuidance() {
      const subscription = this.subscription;
      if (
        !this.$auth || !this.$auth.check() ||
        !subscription || !subscription.plan ||
        subscription.plan.name !== "FREE" || subscription.plan.price !== 0
      ) return null;

      const validCreditValue = (value) =>
        (typeof value === "number" ||
          (typeof value === "string" && value.trim() !== "")) &&
        Number.isFinite(Number(value));
      if (
        !validCreditValue(subscription.total_credits) ||
        !validCreditValue(subscription.remained_credits)
      ) return null;
      const total = Number(subscription.total_credits);
      const remaining = Number(subscription.remained_credits);
      if (total <= 0 || remaining > total) return null;

      // 50% is an experiment threshold. Treat a negative balance as exhausted.
      if (Math.max(0, remaining) / total > 0.5) return null;
      return { exhausted: remaining <= 0 };
    },
    usageValue() {
      return this.subscription
        ? (1 -
            parseFloat(this.subscription.remained_credits) /
              parseFloat(this.subscription.total_credits)) *
            100
        : 0;
    },
    creditsCaption() {
      if (!this.subscription || !this.subscription.total_credits) return "";
      const used =
        this.subscription.total_credits - this.subscription.remained_credits;
      return `${used.toLocaleString()} of ${this.subscription.total_credits.toLocaleString()} used`;
    },
    templates() {
      return [
        {
          name: "curl",
          get_template: templates.CURL.get(this.request_generator, this.api_token),
          prism_language: "bash",
        },
        {
          name: "python",
          get_template: templates.PYTHON.get(this.request_generator, this.api_token),
          prism_language: "python",
        },
        {
          name: "nodejs",
          get_template: templates.NODE_JS.get(this.request_generator, this.api_token),
          prism_language: "javascript",
        },
        {
          name: "php",
          get_template: templates.PHP.get(this.request_generator, this.api_token),
          prism_language: "php",
        },
        {
          name: "java",
          get_template: templates.JAVA.get(this.request_generator, this.api_token),
          prism_language: "java",
        },
        {
          name: "c#",
          get_template: templates.CSHARP.get(this.request_generator, this.api_token),
          prism_language: "csharp",
        },
        {
          name: "proxy",
          get_template: templates.PROXY.get(this.request_generator, this.api_token),
          prism_language: "bash",
        },
      ];
    },
    requestCost() {
      return getRequestCost(this.request_generator);
    },
    isHtmlResponse() {
      return typeof this.raw_result === "string" && !!this.raw_result;
    },
  },
  methods: {
    applyPrefillFromQuery() {
      // Landing-page "run this request" carry-through: ?response_type=markdown&url=https://...&browser=true
      const q = this.$route.query || {};
      if (!this.response_types.some((t) => t.value === q.response_type)) return;
      this.request_generator.response_type = q.response_type;
      if (typeof q.url === "string" && q.url.startsWith("https://")) this.request_generator.url = q.url;
      if (q.browser === "true" || q.browser === "false") {
        this.request_generator.browser = q.browser === "true";
      }
      this.prefilled_from_landing = true;
      if (this.$gtag) this.$gtag.event("landing_example_prefilled", { example: q.response_type });
      this.$nextTick(() => {
        const card = this.$el.querySelector(".wsa-page__generator");
        if (card && card.scrollIntoView) card.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    codeBlockLabel(index) {
      const item = this.tabItems[index];
      if (item === "proxy") return "Proxy credentials";
      return `${item} · GET request`;
    },
    getToken() {
      return this.$store.dispatch("dashboard/getApiToken").then(
        (res) => {
          this.api_token = res.data.token;
        },
        (err) => {
          console.log(err);
        }
      );
    },
    getUsageGraph() {
      this.usage_grapph_loading = true;
      return this.$store.dispatch("dashboard/getUsageGraph").then(
        (res) => {
          this.usage_grapph_loading = false;
          const next = Object.assign({}, this.chartdata);
          next.labels = res.data.labels;
          next.datasets = [buildPrimaryDataset("API credits usage", res.data.data)];
          if (res.data.detailed_view) {
            res.data.detailed_view.forEach((userData, i) => {
              next.datasets.push(
                buildSecondaryDataset(userData.email, userData.data, i)
              );
            });
          }
          this.chartdata = next;
        },
        () => {
          this.usage_grapph_loading = false;
        }
      );
    },
    getCurrentSubscription() {
      return this.$store
        .dispatch("dashboard/getCurrentSubscription")
        .then((res) => {
          this.subscription = res.data.subscriptions[0];
        }, console.error);
    },
    getUTCDateString(timestamp) {
      return getDateUTCString(new Date(timestamp * 1000));
    },
    reload() {
      this.$router.go();
    },
    executeRequestClicked() {
      this.request_dialog_result = "";
      this.request_cost = 0;
      this.result_view_mode = "code";
      this.show_result_dialog = true;
      this.result_loading = true;
      return this.$store
        .dispatch("dashboard/getRequestResult", {
          apiKey: this.api_token,
          generatorData: this.request_generator,
        })
        .then(
          (res) => {
            this.result_loading = false;
            this.raw_result = res.data;
            this.request_dialog_result =
              typeof res.data === "object"
                ? JSON.stringify(res.data, null, 4)
                : res.data;
            this.request_cost = res.headers["ant-credits-cost"]
              ? parseInt(res.headers["ant-credits-cost"])
              : 0;
            // Counted on success only (was: on click), so it means a working request.
            this.$gtag.event(constants.ANALYTICS_EVENTS.EXECUTE_REQUEST, {
              event_category: constants.ANALYTICS_EVENTS.DEFAULT_CATEGORY,
              credits_cost: this.request_cost,
              ...firstTouchParams(),
            });
            this.getCurrentSubscription();
          },
          (err) => {
            this.result_loading = false;
            this.raw_result = err.response.data;
            this.request_dialog_result = err?.response?.data
              ? JSON.stringify(err.response.data, null, 4)
              : "Something went wrong. Please, try again";
          }
        );
    },
    async clipboardForOutput(output) {
      if (typeof output === "object") {
        output = JSON.stringify(output);
      }
      await navigator.clipboard.writeText(output);
      this.$notify({
        group: "main",
        text: "Copied to clipboard!",
        duration: 1200,
      });
    },
    onUpdatePaymentDetailsClicked() {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: this.subscription.update_url,
        successCallback: () => {},
        closeCallback: () => {},
      });
    },
    getAPIRenewStatus() {
      return this.$store
        .dispatch("dashboard/getAPIRenewStatus")
        .then((res) => {
          this.api_renew_status = res.auto_prolong_web_scraping_api;
        }, console.error);
    },
    onChangeRenewStatusClicked() {
      this.$store
        .dispatch("dashboard/updateSubscriptionRenewStatus", {
          auto_prolong_web_scraping_api: !this.api_renew_status,
        })
        .then(() => {
          this.api_renew_status = !this.api_renew_status;
        });
    },
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.getToken(),
      this.getCurrentSubscription(),
      this.getAPIRenewStatus(),
    ]).then(
      () => (this.loading = false),
      () => (this.loading = false)
    );
    this.getUsageGraph();
    this.applyPrefillFromQuery();
  },
};
</script>

<style scoped lang="scss">
.wsa-page {
  position: relative;

  &__card {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__meta {
    margin: 0 0 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__meta-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 1.5;

    dt {
      color: rgba(15, 23, 42, 0.58);
      font-weight: 500;
      margin: 0;
    }

    dd {
      color: #0f172a;
      font-weight: 500;
      margin: 0;
      text-align: right;

      a {
        color: #e2782f;
        font-weight: 600;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  &__credentials {
    margin-bottom: 18px;
  }

  &__stat {
    margin-top: auto;
  }

  &__hint {
    font-size: 12px;
    color: rgba(15, 23, 42, 0.62);
    line-height: 1.5;
    margin: 14px 0 0;

    a {
      color: #e2782f;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__plan-guidance {
    margin-top: 16px;
    padding: 12px 16px;
    border: 1px solid #ead9ca;
    border-radius: 8px;
    background: #fff8f2;
    color: #334155;
    font-size: 13px;
    line-height: 1.6;

    h3 {
      margin: 0 0 4px;
      color: #0f172a;
      font-size: 14px;
      font-weight: 600;
    }

    p {
      margin: 0;
    }

    a {
      color: #974817;
      text-decoration: underline;

      &:focus-visible {
        outline: 2px solid #974817;
        outline-offset: 3px;
        border-radius: 2px;
      }
    }

    > a {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      font-weight: 600;
    }
  }

  &__empty {
    font-size: 14px;
    color: rgba(15, 23, 42, 0.62);
    margin: 0;

    a {
      color: #e2782f;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__side {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;

    .section-card {
      flex: 1;
    }
  }

  &__side-status {
    font-size: 14px;
    color: rgba(15, 23, 42, 0.72);
    margin: 0;
  }

  &__generator {
    margin-top: 20px;
  }

  &__switch-label {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.72);
    margin-right: 6px;
  }

  &__help {
    color: rgba(15, 23, 42, 0.45) !important;
  }

  &__field-hint {
    font-size: 11px;
    color: rgba(15, 23, 42, 0.55);
    line-height: 1.5;
    margin: 6px 0 0;

    a {
      color: #e2782f;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__execute {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 24px;
    flex-wrap: wrap;
  }

  &__cost {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.72);

    strong {
      color: #0f172a;
      font-weight: 700;
    }
  }

  &__code-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__tabs {
    ::v-deep .v-tabs-bar {
      background: transparent !important;
    }
  }

  &__tab {
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    font-size: 13px;
  }

  &__tab-items {
    background: transparent !important;
  }

  &__chart {
    margin-top: 20px;
  }

  &__result-card {
    border-radius: 12px;
    overflow: hidden;
  }

  &__result-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 16px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);

    h2 {
      font-size: 18px;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }
  }

  &__result-body {
    padding: 20px 24px;
    max-height: 560px;
    overflow: auto;
  }

  &__preview {
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;

    iframe {
      width: 100%;
      height: 450px;
      border: none;
      background: #ffffff;
      display: block;
    }
  }

  &__result-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;

    h3 {
      font-size: 14px;
      color: rgba(15, 23, 42, 0.62);
      font-weight: 500;
    }
  }

  &__result-cost {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.72);
    margin: 14px 0 0;

    strong {
      color: #0f172a;
    }
  }

  &__result-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 24px 0;
  }

  &__result-actions {
    display: flex;
    align-items: center;
    padding: 12px 24px 20px;
    gap: 12px;
  }
}
</style>
